import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function fmt(cents) {
  return '$' + Math.abs(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  });
}

function UtilizationBar({ pct }) {
  const color = pct > 80 ? '#ff082f' : pct > 60 ? '#f59e0b' : '#3a6ad9';
  return (
    <div style={{ background: '#f3f4f6', borderRadius: 999, height: 6, width: '100%', overflow: 'hidden', marginTop: 6 }}>
      <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: color, borderRadius: 999 }} />
    </div>
  );
}

const STATUS_COLORS = {
  draft:          { bg: '#f3f4f6', color: '#374151' },
  pending_review: { bg: '#fef9c3', color: '#854d0e' },
  approved:       { bg: '#eff6ff', color: '#1e40af' },
  sent:           { bg: '#dcfce7', color: '#166534' },
};

const REPORT_TYPE_LABELS = {
  borrowing_base:       'Borrowing Base Certificate',
  covenant_compliance:  'Covenant Compliance Certificate',
  financial_statements: 'Financial Statements',
  custom:               'Custom Report',
};

// Extracted as its own component so each row owns its `showBody` state —
// calling useState inside a .map callback would violate Rules of Hooks.
function ReportRow({ report, onApprove, onReject, onSend }) {
  const [showBody, setShowBody] = useState(false);
  const sc = STATUS_COLORS[report.status] || STATUS_COLORS.draft;

  return (
    <div style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 12px', border: '1px solid #e5e7eb' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <div style={{ fontSize: 11, color: '#374151', fontWeight: 600 }}>
          {report.period_start} → {report.period_end}
          {report.due_date && (
            <span style={{ marginLeft: 8, color: '#9ca3af', fontWeight: 400 }}>Due {report.due_date}</span>
          )}
        </div>
        <span style={{ fontSize: 10, fontWeight: 600, background: sc.bg, color: sc.color, borderRadius: 999, padding: '2px 8px' }}>
          {report.status.replace('_', ' ').replace(/^\w/, c => c.toUpperCase())}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button onClick={() => setShowBody(s => !s)}
          style={{ background: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 10px', fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>
          {showBody ? 'Hide' : 'Preview'}
        </button>
        <button onClick={() => { navigator.clipboard.writeText(report.body); alert('Copied to clipboard'); }}
          style={{ background: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 10px', fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>
          Copy
        </button>

        {report.status === 'pending_review' && (
          <>
            <button onClick={() => onApprove(report.id)}
              style={{ background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>
              Approve
            </button>
            <button onClick={() => {
              const reason = window.prompt('Reason for rejection:');
              if (reason !== null) onReject(report.id, reason);
            }}
              style={{ background: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 10px', fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>
              Reject
            </button>
          </>
        )}
        {report.status === 'approved' && (
          <button onClick={() => onSend(report.id)}
            style={{ background: '#acf44e', color: '#111827', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
            Send to Lender →
          </button>
        )}
        {report.status === 'sent' && (
          <span style={{ fontSize: 10, color: '#166534', fontWeight: 600, padding: '4px 0' }}>
            ✓ Sent{report.sent_at ? ` ${new Date(report.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
          </span>
        )}
      </div>

      {showBody && (
        <div style={{
          marginTop: 10, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6,
          padding: '10px 12px', fontSize: 10, fontFamily: 'monospace', whiteSpace: 'pre-wrap',
          color: '#374151', lineHeight: 1.6, maxHeight: 300, overflow: 'auto',
        }}>
          {report.body}
        </div>
      )}
    </div>
  );
}

export default function AdminWarehouse() {
  const [facilities, setFacilities] = useState([]);
  const [expanded, setExpanded]     = useState(null);
  const [details, setDetails]       = useState({});
  const [reports, setReports]       = useState({});
  const [loading, setLoading]       = useState(true);
  const [generating, setGenerating] = useState({});
  const [showAddReq, setShowAddReq] = useState(null);
  const [reqForm, setReqForm] = useState({
    report_type: 'borrowing_base',
    report_name: '',
    frequency: 'quarterly',
    due_days_after_period: 15,
    contact_name: '',
    contact_email: '',
    custom_instructions: '',
  });

  const token = localStorage.getItem('doorly_admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchFacilities = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/credit-facilities`, { headers });
      const wh = (res.data || []).filter(f => f.facility_type === 'warehouse' && f.active);
      setFacilities(wh);
    } catch (err) {
      console.error('Warehouse fetch failed:', err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchDetail = async (facilityId) => {
    try {
      const [detRes, repRes] = await Promise.all([
        axios.get(`${API}/api/admin/credit-facilities/${facilityId}`, { headers }),
        axios.get(`${API}/api/admin/credit-facilities/${facilityId}/reports`, { headers }),
      ]);
      setDetails(d => ({ ...d, [facilityId]: detRes.data }));
      setReports(r => ({ ...r, [facilityId]: repRes.data }));
    } catch (err) {
      console.error('Detail fetch failed:', err.message);
    }
  };

  useEffect(() => { fetchFacilities(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExpand = async (facilityId) => {
    if (expanded === facilityId) {
      setExpanded(null);
    } else {
      setExpanded(facilityId);
      if (!details[facilityId]) await fetchDetail(facilityId);
    }
  };

  const handleGenerateReport = async (facilityId, req) => {
    const now = new Date();
    const quarter = Math.floor(now.getMonth() / 3);
    const defaultStart = new Date(now.getFullYear(), quarter * 3, 1).toISOString().split('T')[0];
    const defaultEnd   = new Date(now.getFullYear(), quarter * 3 + 3, 0).toISOString().split('T')[0];

    const start = window.prompt('Period start (YYYY-MM-DD):', defaultStart);
    if (!start) return;
    const end = window.prompt('Period end (YYYY-MM-DD):', defaultEnd);
    if (!end) return;

    setGenerating(g => ({ ...g, [req.id]: true }));
    try {
      await axios.post(
        `${API}/api/admin/credit-facilities/${facilityId}/reports/generate`,
        { requirement_id: req.id, period_start: start, period_end: end },
        { headers }
      );
      await fetchDetail(facilityId);
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setGenerating(g => ({ ...g, [req.id]: false }));
    }
  };

  const handleReportAction = async (facilityId, reportId, action, payload = {}) => {
    try {
      await axios.patch(
        `${API}/api/admin/credit-facilities/reports/${reportId}/${action}`,
        payload, { headers }
      );
      await fetchDetail(facilityId);
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  const handleAddRequirement = async (facilityId) => {
    try {
      await axios.post(`${API}/api/admin/credit-facilities/${facilityId}/requirements`, reqForm, { headers });
      setShowAddReq(null);
      setReqForm({
        report_type: 'borrowing_base', report_name: '', frequency: 'quarterly',
        due_days_after_period: 15, contact_name: '', contact_email: '', custom_instructions: '',
      });
      await fetchDetail(facilityId);
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  // Aggregate totals across all warehouse facilities for the summary bar.
  const totalLimit     = facilities.reduce((s, f) => s + parseInt(f.credit_limit_cents || 0), 0);
  const totalDrawn     = facilities.reduce((s, f) => s + parseInt(f.balance_cents || 0), 0);
  const totalAvailable = totalLimit - totalDrawn;
  const totalUtilization = totalLimit > 0 ? Math.round((totalDrawn / totalLimit) * 10000) / 100 : 0;

  return (
    <AdminLayout>
      <div style={{ padding: '24px 32px' }}>
        {/* Header */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 2 }}>Finance</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>Warehouse</h1>
        </div>

        {loading && <div style={{ color: '#6b7280', fontSize: 13 }}>Loading...</div>}

        {/* Summary bar */}
        {!loading && facilities.length > 0 && (
          <div style={{ background: '#111827', borderRadius: 12, padding: '16px 22px', marginBottom: 20, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
            {[
              { label: 'Total Capacity',      value: fmt(totalLimit) },
              { label: 'Total Drawn',         value: fmt(totalDrawn) },
              { label: 'Total Available',     value: fmt(totalAvailable) },
              { label: 'Overall Utilization', value: `${totalUtilization}%` },
            ].map(m => (
              <div key={m.label}>
                <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{m.label}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>{m.value}</div>
              </div>
            ))}
          </div>
        )}

        {!loading && facilities.length === 0 && (
          <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 32, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
            No warehouse facilities configured
          </div>
        )}

        {/* Facility cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16, marginBottom: 20 }}>
          {facilities.map(f => (
            <div key={f.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 18px' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 2 }}>{f.lender_name}</div>
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 12 }}>{f.name}</div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                {[
                  { label: 'Limit',        value: fmt(f.credit_limit_cents) },
                  { label: 'Drawn',        value: fmt(f.balance_cents) },
                  { label: 'Available',    value: fmt(f.available_cents) },
                  { label: 'Active Loans', value: f.active_positions?.length || 0 },
                ].map(m => (
                  <div key={m.label} style={{ background: '#f9fafb', borderRadius: 8, padding: '8px 10px' }}>
                    <div style={{ fontSize: 9, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{m.label}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{m.value}</div>
                  </div>
                ))}
              </div>

              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#6b7280', marginBottom: 4 }}>
                  <span>Utilization</span>
                  <span style={{ fontWeight: 700, color: '#111827' }}>{f.utilization_pct}%</span>
                </div>
                <UtilizationBar pct={f.utilization_pct} />
              </div>

              <button onClick={() => handleExpand(f.id)}
                style={{ width: '100%', background: '#f9fafb', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, padding: '7px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                {expanded === f.id ? 'Hide Details ↑' : 'View Details ↓'}
              </button>
            </div>
          ))}
        </div>

        {/* Expanded detail */}
        {expanded && details[expanded] && (() => {
          const d = details[expanded];
          const facilityReports = reports[expanded] || [];

          return (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', marginBottom: 20 }}>
              <div style={{ background: '#111827', padding: '14px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{d.lender_name} — Loan Positions</div>
                <button onClick={() => setExpanded(null)}
                  style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 18 }}>×</button>
              </div>

              {/* Positions table */}
              {d.positions?.filter(p => p.status === 'active').length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: 12, fontStyle: 'italic' }}>
                  No active loan positions on this line
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb' }}>
                      {['Loan', 'Borrower', 'Amount', 'Drawn Date', 'Days on Line', ''].map(h => (
                        <th key={h} style={{ padding: '8px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {d.positions.filter(p => p.status === 'active').map((pos, i) => (
                      <tr key={pos.id} style={{ borderTop: i > 0 ? '1px solid #f3f4f6' : 'none' }}>
                        <td style={{ padding: '10px 16px', fontSize: 12, fontWeight: 600, color: '#3a6ad9' }}>{pos.loan_number || '—'}</td>
                        <td style={{ padding: '10px 16px', fontSize: 12, color: '#374151' }}>{pos.borrower_name || '—'}</td>
                        <td style={{ padding: '10px 16px', fontSize: 12, fontWeight: 700, color: '#111827' }}>{fmt(pos.drawn_amount_cents)}</td>
                        <td style={{ padding: '10px 16px', fontSize: 12, color: '#6b7280' }}>
                          {new Date(pos.drawn_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </td>
                        <td style={{ padding: '10px 16px' }}>
                          <span style={{
                            fontSize: 11, fontWeight: 600,
                            background: pos.days_on_line > 30 ? '#fef2f2' : '#f0fdf4',
                            color: pos.days_on_line > 30 ? '#991b1b' : '#166534',
                            borderRadius: 6, padding: '2px 8px',
                          }}>
                            {pos.days_on_line === 0 ? 'Today' : `${pos.days_on_line}d`}
                          </span>
                        </td>
                        <td style={{ padding: '10px 16px' }}>
                          <button onClick={async () => {
                            const amount = window.prompt('Payoff amount ($):');
                            if (!amount) return;
                            try {
                              await axios.patch(
                                `${API}/api/admin/credit-facilities/positions/${pos.id}/payoff`,
                                { paid_off_amount_cents: Math.round(parseFloat(amount) * 100) },
                                { headers }
                              );
                              await fetchDetail(expanded);
                              await fetchFacilities();
                            } catch (err) {
                              alert(err.response?.data?.error || err.message);
                            }
                          }}
                            style={{ background: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                            Record Payoff
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Reporting requirements */}
              <div style={{ borderTop: '2px solid #f3f4f6', padding: '16px 22px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>Reporting Requirements</div>
                  <button onClick={() => setShowAddReq(showAddReq === expanded ? null : expanded)}
                    style={{ background: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                    + Add Requirement
                  </button>
                </div>

                {showAddReq === expanded && (
                  <div style={{ background: '#f9fafb', borderRadius: 10, padding: '16px', marginBottom: 16, border: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 10 }}>
                      <div>
                        <label style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, display: 'block', marginBottom: 3 }}>Report Type</label>
                        <select value={reqForm.report_type}
                          onChange={e => setReqForm(f => ({ ...f, report_type: e.target.value, report_name: REPORT_TYPE_LABELS[e.target.value] || '' }))}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 11, boxSizing: 'border-box' }}>
                          {Object.entries(REPORT_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, display: 'block', marginBottom: 3 }}>Report Name</label>
                        <input type="text" value={reqForm.report_name}
                          onChange={e => setReqForm(f => ({ ...f, report_name: e.target.value }))}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 11, boxSizing: 'border-box' }} />
                      </div>
                      <div>
                        <label style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, display: 'block', marginBottom: 3 }}>Frequency</label>
                        <select value={reqForm.frequency}
                          onChange={e => setReqForm(f => ({ ...f, frequency: e.target.value }))}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 11, boxSizing: 'border-box' }}>
                          <option value="monthly">Monthly</option>
                          <option value="quarterly">Quarterly</option>
                          <option value="annual">Annual</option>
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, display: 'block', marginBottom: 3 }}>Due (days after period end)</label>
                        <input type="number" value={reqForm.due_days_after_period}
                          onChange={e => setReqForm(f => ({ ...f, due_days_after_period: parseInt(e.target.value) }))}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 11, boxSizing: 'border-box' }} />
                      </div>
                      <div>
                        <label style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, display: 'block', marginBottom: 3 }}>Contact Email</label>
                        <input type="email" value={reqForm.contact_email}
                          onChange={e => setReqForm(f => ({ ...f, contact_email: e.target.value }))}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 11, boxSizing: 'border-box' }} />
                      </div>
                      <div>
                        <label style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, display: 'block', marginBottom: 3 }}>Custom Instructions</label>
                        <input type="text" value={reqForm.custom_instructions}
                          onChange={e => setReqForm(f => ({ ...f, custom_instructions: e.target.value }))}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 11, boxSizing: 'border-box' }} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                      <button onClick={() => setShowAddReq(null)}
                        style={{ background: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 7, padding: '6px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                        Cancel
                      </button>
                      <button onClick={() => handleAddRequirement(expanded)}
                        style={{ background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 7, padding: '6px 14px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                        Add Requirement
                      </button>
                    </div>
                  </div>
                )}

                {(!d.requirements || d.requirements.length === 0) ? (
                  <div style={{ color: '#9ca3af', fontSize: 12, fontStyle: 'italic', padding: '8px 0' }}>
                    No reporting requirements configured
                  </div>
                ) : (
                  d.requirements.map(req => {
                    const reqReports = facilityReports.filter(r => r.requirement_id === req.id);
                    return (
                      <div key={req.id} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px', marginBottom: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 2 }}>{req.report_name}</div>
                            <div style={{ fontSize: 11, color: '#6b7280' }}>
                              {req.frequency.charAt(0).toUpperCase() + req.frequency.slice(1)} · Due {req.due_days_after_period} days after period end
                              {req.contact_email && <span> · {req.contact_email}</span>}
                            </div>
                          </div>
                          <button disabled={generating[req.id]}
                            onClick={() => handleGenerateReport(expanded, req)}
                            style={{
                              background: generating[req.id] ? '#9ca3af' : '#acf44e',
                              color: '#111827', border: 'none', borderRadius: 7, padding: '6px 14px',
                              fontSize: 11, fontWeight: 700,
                              cursor: generating[req.id] ? 'default' : 'pointer',
                            }}>
                            {generating[req.id] ? 'Generating...' : '+ Generate Report'}
                          </button>
                        </div>

                        {reqReports.length === 0 ? (
                          <div style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic' }}>No reports generated yet</div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {reqReports.map(report => (
                              <ReportRow key={report.id} report={report}
                                onApprove={(id) => handleReportAction(expanded, id, 'approve')}
                                onReject={(id, reason) => handleReportAction(expanded, id, 'reject', { reason })}
                                onSend={(id) => handleReportAction(expanded, id, 'send')} />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })()}
      </div>
    </AdminLayout>
  );
}
